import { useEffect, useRef, useState } from "react";
import { useFeature } from "./features";

// Talk to build (decision 56, feature `talkToBuild`): the mic button types what the student says
// into the message box. Chrome sends the audio to Google to make words, so the first press shows a
// note, and nothing goes to the helper until the student presses Send.

type SpeechEvent = { results: ArrayLike<ArrayLike<{ transcript: string }>> };
type Recognition = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((e: SpeechEvent) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
};
type RecognitionCtor = new () => Recognition;

const FIRST_NOTE = "Your voice goes to Google to turn it into words. Nothing is sent until you press Send.";
const SEEN_KEY = "buildlab.talkNoteSeen";
const SILENCE_MS = 6000;

function speechCtor(): RecognitionCtor | null {
  const w = window as unknown as { SpeechRecognition?: RecognitionCtor; webkitSpeechRecognition?: RecognitionCtor };
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

// Browser storage is only a convenience here: in a private window the note just shows again.
function noteSeen(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) === "1";
  } catch {
    return false;
  }
}

function markNoteSeen(): void {
  try {
    localStorage.setItem(SEEN_KEY, "1");
  } catch {
    /* ignore: see noteSeen */
  }
}

type Props = { draft: string; setDraft: (text: string) => void; disabled?: boolean };

export function TalkButton({ draft, setDraft, disabled }: Props) {
  const on = useFeature("talkToBuild");
  const [Ctor] = useState<RecognitionCtor | null>(() => speechCtor());
  const [listening, setListening] = useState(false);
  const [notice, setNotice] = useState<{ text: string; error?: boolean } | null>(null);
  const rec = useRef<Recognition | null>(null);
  const silence = useRef<number | undefined>(undefined);

  // The note goes away once the box is emptied by sending.
  useEffect(() => {
    if (!draft) setNotice(null);
  }, [draft]);

  // Leaving the page should not keep the microphone open.
  useEffect(
    () => () => {
      window.clearTimeout(silence.current);
      rec.current?.abort();
    },
    [],
  );

  if (!on || !Ctor) return null;

  function stop() {
    rec.current?.stop();
  }

  function start() {
    if (!Ctor) return;
    if (!noteSeen()) {
      markNoteSeen();
      setNotice({ text: FIRST_NOTE });
    } else {
      setNotice(null);
    }

    const base = draft.trim();
    const r = new Ctor();
    r.lang = "en-US";
    r.continuous = true;
    r.interimResults = true;

    // Stop after a short quiet spell, so the mic doesn't stay open by accident.
    const kick = () => {
      window.clearTimeout(silence.current);
      silence.current = window.setTimeout(stop, SILENCE_MS);
    };

    r.onresult = (e) => {
      const said = Array.from(e.results, (res) => res[0]?.transcript ?? "")
        .join("")
        .trim();
      setDraft([base, said].filter(Boolean).join(" "));
      kick();
    };
    r.onerror = (e) => {
      if (e.error === "no-speech" || e.error === "aborted") return;
      const blocked = e.error === "not-allowed" || e.error === "service-not-allowed";
      setNotice({
        text: blocked ? "BuildLab can't use your microphone. Check the microphone setting in your browser, then try again." : "Talking didn't work this time. You can type instead.",
        error: true,
      });
    };
    r.onend = () => {
      window.clearTimeout(silence.current);
      rec.current = null;
      setListening(false);
    };

    rec.current = r;
    try {
      r.start();
      setListening(true);
      kick();
    } catch {
      rec.current = null;
      setNotice({ text: "Talking didn't work this time. You can type instead.", error: true });
    }
  }

  return (
    <>
      {notice && (
        <p className={`talk-note${notice.error ? " error" : ""}`} role="status">
          {notice.text}
        </p>
      )}
      <button
        type="button"
        className={`talk-btn${listening ? " listening" : ""}`}
        aria-pressed={listening}
        aria-label={listening ? "Stop talking" : "Talk to build"}
        onClick={() => (listening ? stop() : start())}
        disabled={disabled}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="9" y="3" width="6" height="11" rx="3" />
          <path d="M5 11a7 7 0 0 0 14 0" />
          <path d="M12 18v3" />
        </svg>
        {listening ? "Stop" : "Talk"}
      </button>
    </>
  );
}
